/**
 * KitchenTicketDetailPage: one kitchen ticket (ADR 0027): header, its items (quantity, name, options, note, allergy
 * note) and, at the store, the print attempts with a Reprint button.
 *
 * Props: `mode`. At the store the page reloads every 15 seconds and Reprint needs `store.kitchen.reprint` (a plain
 * Button; the result shows in a notification). At HO the ticket is read-only, labelled "Finished orders only, may
 * lag", with no print attempts and no actions.
 */
import {
  Button,
  InlineNotification,
  Link as CarbonLink,
  SkeletonText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  Tag,
} from '@carbon/react';
import { Printer } from '@carbon/icons-react';
import { useTranslation } from '@pos/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { useAdmin } from '../adminContext';
import { BackLink } from '../components/BackLink';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { useFormat } from '../orders/model';
import { LagNotice } from '../overview/widgets';
import { asProblem, problemMessage } from '../problems';
import { hasPermission } from '../session';
import type { KitchenTicketDetail } from './kitchenApi';
import {
  KITCHEN_POLL_MS,
  KITCHEN_REPRINT_PERMISSION,
  kitchenKey,
  useKitchenApi,
  type KitchenMode,
} from './model';
import { PrintStatus } from './KitchenTicketsPage';
import { KitchenStatusTag } from './KitchenStatusTag';
import './kitchen.scss';

const NOTICE_MS = 8000;

export function KitchenTicketDetailPage({ mode }: { mode: KitchenMode }) {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const api = useKitchenApi();
  const ticket = useQuery({
    queryKey: [...kitchenKey, 'ticket', id],
    queryFn: () => api.ticket(id),
    refetchInterval: mode === 'store' ? KITCHEN_POLL_MS : false,
    refetchIntervalInBackground: false,
  });

  return (
    <section className="pos-admin-page">
      <BackLink to="/kitchen/tickets">{t('admin.kitchen.tickets.back')}</BackLink>
      {ticket.error ? (
        <InlineNotification kind="error" role="alert" lowContrast hideCloseButton title={problemMessage(t, asProblem(ticket.error))} />
      ) : !ticket.data ? (
        <SkeletonText heading paragraph lineCount={6} />
      ) : (
        <Detail detail={ticket.data} mode={mode} />
      )}
    </section>
  );
}

function Detail({ detail, mode }: { detail: KitchenTicketDetail; mode: KitchenMode }) {
  const { t } = useTranslation();
  const { session } = useAdmin();
  const format = useFormat();
  const api = useKitchenApi();
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<string>();
  const k = detail.ticket;
  const canReprint = mode === 'store' && hasPermission(session, KITCHEN_REPRINT_PERMISSION);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(undefined), NOTICE_MS);
    return () => clearTimeout(timer);
  }, [notice]);

  const reprint = useMutation({
    mutationFn: () => api.reprint(k.id),
    onSuccess: (updated) => {
      queryClient.setQueryData([...kitchenKey, 'ticket', k.id], updated);
      void queryClient.invalidateQueries({ queryKey: [...kitchenKey, 'tickets'] });
      setNotice(t('admin.kitchen.detail.reprinted'));
    },
  });

  const facts: [string, ReactNode][] = [
    [
      t('admin.kitchen.columns.order'),
      <CarbonLink as={Link} to={`/orders/${k.orderId}`} key="order">
        {k.orderNumber}
      </CarbonLink>,
    ],
    [t('admin.kitchen.columns.station'), `${k.stationCode} · ${k.stationName}`],
    [t('admin.kitchen.columns.label'), k.headerLabel],
    [t('admin.kitchen.columns.saleType'), k.saleTypeCode],
    [t('admin.kitchen.columns.firedAt'), format.dateTime(k.firedAt)],
    ...(mode === 'store' && k.printedAt
      ? ([[t('admin.kitchen.detail.printedAt'), format.dateTime(k.printedAt)]] as [string, ReactNode][])
      : []),
    ...(Number(k.reprintCount) > 0
      ? ([[t('admin.kitchen.detail.reprints'), format.number(k.reprintCount)]] as [string, ReactNode][])
      : []),
    ...(detail.orderNote ? ([[t('admin.kitchen.detail.orderNote'), detail.orderNote]] as [string, ReactNode][]) : []),
  ];

  return (
    <>
      <PageHeader
        title={t('admin.kitchen.detail.title', { station: k.stationCode, number: k.ticketNumber })}
        tag={
          <span className="pos-kitchen__tags">
            <KitchenStatusTag kind="ticket" status={k.status} />
            {k.isAddition && <Tag type="gray">{t('admin.kitchen.addition')}</Tag>}
            {mode === 'store' && <PrintStatus ticket={k} />}
          </span>
        }
        actions={
          mode === 'ho' ? (
            <LagNotice />
          ) : canReprint ? (
            <Button renderIcon={Printer} disabled={reprint.isPending} onClick={() => reprint.mutate()}>
              {t('admin.kitchen.detail.reprint')}
            </Button>
          ) : undefined
        }
      />
      {reprint.error && (
        <InlineNotification
          kind="error"
          role="alert"
          lowContrast
          hideCloseButton
          title={problemMessage(t, asProblem(reprint.error))}
        />
      )}
      {notice && <InlineNotification kind="success" role="status" lowContrast hideCloseButton title={notice} />}
      <dl className="pos-kitchen__facts">
        {facts.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>

      <h2 className="pos-kitchen__section-title">{t('admin.kitchen.detail.items')}</h2>
      <TableContainer>
        <Table aria-label={t('admin.kitchen.detail.items')}>
          <TableHead>
            <TableRow>
              <TableHeader>{t('admin.kitchen.detail.quantity')}</TableHeader>
              <TableHeader>{t('admin.kitchen.detail.item')}</TableHeader>
              <TableHeader>{t('admin.kitchen.detail.options')}</TableHeader>
              <TableHeader>{t('admin.kitchen.detail.note')}</TableHeader>
              <TableHeader>{t('admin.kitchen.detail.allergy')}</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {detail.items.map((item) => (
              <TableRow key={item.id}>
                <TableCell className="pos-admin-page__number">{format.number(item.quantity)}</TableCell>
                <TableCell>
                  {item.uomName ? `${item.name} (${item.uomName})` : item.name}
                  {item.parentName && <span className="pos-kitchen__muted"> {t('admin.kitchen.detail.partOf', { name: item.parentName })}</span>}
                </TableCell>
                <TableCell>{item.modifiersText}</TableCell>
                <TableCell>{item.note}</TableCell>
                <TableCell>{item.allergyNote}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {mode === 'store' && (
        <>
          <h2 className="pos-kitchen__section-title">{t('admin.kitchen.detail.attempts')}</h2>
          {detail.printJobs.length === 0 ? (
            <EmptyState headingLevel={3} heading={t('admin.kitchen.detail.noAttempts')} />
          ) : (
            <TableContainer>
              <Table aria-label={t('admin.kitchen.detail.attempts')}>
                <TableHead>
                  <TableRow>
                    <TableHeader>{t('admin.kitchen.detail.time')}</TableHeader>
                    <TableHeader>{t('admin.kitchen.jobs.device')}</TableHeader>
                    <TableHeader>{t('admin.kitchen.jobs.status')}</TableHeader>
                    <TableHeader>{t('admin.kitchen.jobs.attempts')}</TableHeader>
                    <TableHeader>{t('admin.kitchen.jobs.error')}</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...detail.printJobs]
                    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
                    .map((job) => (
                      <TableRow key={job.id}>
                        <TableCell>{format.dateTime(job.printedAt ?? job.createdAt)}</TableCell>
                        <TableCell>
                          {job.deviceName}
                          {job.fallbackOfJobId && <Tag type="gray">{t('admin.kitchen.jobs.fallback')}</Tag>}
                          {job.reprintOfJobId && <Tag type="gray">{t('admin.kitchen.jobs.reprint')}</Tag>}
                        </TableCell>
                        <TableCell>
                          <KitchenStatusTag kind="job" status={job.status} />
                        </TableCell>
                        <TableCell className="pos-admin-page__number">{format.number(job.attempts)}</TableCell>
                        <TableCell>{job.lastError}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </>
  );
}
